import React, { useEffect, useState, useMemo } from 'react';
import { ProtectAPi } from '../../../api/ProtectAPi';
import { ServerUrl } from '../../../api/ServerUrl';
import BackButton from '../../tools/BackButton';
import JoditEditor from "jodit-react";
import AdminQuickNav from './AdminQuickNav';

import swal from 'sweetalert';
import { Link } from 'react-router-dom';

const LandingContentEdit = () => {
  const httpCPage = ServerUrl;
  const { http } = useMemo(() => ProtectAPi(), []);

  const [loader, setLoader] = useState('off'); // central loader
  // per-section loading state
  const [sectionLoading, setSectionLoading] = useState({
    banner: false,
    tga: false,
    about: false,
    doctor: false,
    pedorthist: false,
  });

  const [id, setId] = useState(null);
  const [errors, setErrors] = useState({});
  const [content, setContent] = useState({
    // Banner
    b_title1_en: '',
    b_title2_en: '',
    b_title3_en: '',
    b_title1_bn: '',
    b_title2_bn: '',
    b_title3_bn: '',
    b_image: null,
    b_alt: '',
    // T section (TGA)
    t_title_en: '',
    t_title_bn: '',
    t_image: null,
    t_alt: '',
    // A section (About)
    a_title_en: '',
    a_title_bn: '',
    a_image: null,
    a_alt: '',
    // D (Doctor)
    d_name_en: '',
    d_name_bn: '',
    d_designation_en: '',
    d_designation_bn: '',
    d_title_en: '',
    d_title_bn: '',
    d_description_en: '',
    d_description_bn: '',
    d_image: null,
    d_alt: '',
    p_title_en: '',
    p_title_bn: '',
  });

  // images (file objects)
  const [bImageFile, setBImageFile] = useState(null);
  const [tImageFile, setTImageFile] = useState(null);
  const [aImageFile, setAImageFile] = useState(null);
  const [dImageFile, setDImageFile] = useState(null);

  // image previews (urls)
  const [bImagePreview, setBImagePreview] = useState('');
  const [tImagePreview, setTImagePreview] = useState('');
  const [aImagePreview, setAImagePreview] = useState('');
  const [dImagePreview, setDImagePreview] = useState('');

  const placeholder = 'Start typing...';
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: placeholder || 'Start typing...',
      uploader: { insertImageAsBase64URI: true },
      height: 300,
      showXPathInStatusbar: false,
      toolbarAdaptive: true,
    }),
    [placeholder]
  );

  useEffect(() => {
    http
      .get('/landing-page-content')
      .then(res => {
        let record = null;
        if (Array.isArray(res.data) && res.data.length > 0) {
          record = res.data[0];
        } else if (res.data && typeof res.data === 'object') {
          record = res.data;
        }

        if (record) {
          setId(record.id || record.ID || null);

          setContent(prev => ({
            ...prev,
            b_title1_en: record.b_title1_en || '',
            b_title2_en: record.b_title2_en || '',
            b_title3_en: record.b_title3_en || '',
            b_title1_bn: record.b_title1_bn || '',
            b_title2_bn: record.b_title2_bn || '',
            b_title3_bn: record.b_title3_bn || '',
            b_alt: record.b_alt || '',
            t_title_en: record.t_title_en || '',
            t_title_bn: record.t_title_bn || '',
            t_alt: record.t_alt || '',
            a_title_en: record.a_title_en || '',
            a_title_bn: record.a_title_bn || '',
            a_alt: record.a_alt || '',
            d_name_en: record.d_name_en || '',
            d_name_bn: record.d_name_bn || '',
            d_designation_en: record.d_designation_en || '',
            d_designation_bn: record.d_designation_bn || '',
            d_title_en: record.d_title_en || '',
            d_title_bn: record.d_title_bn || '',
            d_description_en: record.d_description_en || '',
            d_description_bn: record.d_description_bn || '',
            d_alt: record.d_alt || '',
            p_title_en: record.p_title_en || '',
            p_title_bn: record.p_title_bn || '',
          }));

          if (record.b_image) setBImagePreview(`${httpCPage}/backend/img/${record.b_image}`);
          if (record.t_image) setTImagePreview(`${httpCPage}/backend/img/${record.t_image}`);
          if (record.a_image) setAImagePreview(`${httpCPage}/backend/img/${record.a_image}`);
          if (record.d_image) setDImagePreview(`${httpCPage}/backend/img/${record.d_image}`);
        }
      })
      .catch(err => {
        console.error('Error loading landing content:', err);
      });
    // fetch once
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = e => {
    const { name, value } = e.target;
    setContent(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const previewURL = URL.createObjectURL(file);

    if (field === 'b_image') {
      setBImageFile(file);
      setBImagePreview(previewURL);
      setContent(prev => ({ ...prev, b_image: file }));
    } else if (field === 't_image') {
      setTImageFile(file);
      setTImagePreview(previewURL);
      setContent(prev => ({ ...prev, t_image: file }));
    } else if (field === 'a_image') {
      setAImageFile(file);
      setAImagePreview(previewURL);
      setContent(prev => ({ ...prev, a_image: file }));
    } else if (field === 'd_image') {
      setDImageFile(file);
      setDImagePreview(previewURL);
      setContent(prev => ({ ...prev, d_image: file }));
    }
  };

  const removeImage = field => {
    if (field === 'b_image') {
      setBImageFile(null);
      setBImagePreview('');
      setContent(prev => ({ ...prev, b_image: null }));
    } else if (field === 't_image') {
      setTImageFile(null);
      setTImagePreview('');
      setContent(prev => ({ ...prev, t_image: null }));
    } else if (field === 'a_image') {
      setAImageFile(null);
      setAImagePreview('');
      setContent(prev => ({ ...prev, a_image: null }));
    } else if (field === 'd_image') {
      setDImageFile(null);
      setDImagePreview('');
      setContent(prev => ({ ...prev, d_image: null }));
    }
  };

  const handleEditorBlur = (field, newContent) => {
    setContent(prev => ({ ...prev, [field]: newContent }));
  };

  // keys per section (only DB fields)
  const keysBySection = {
    banner: ['b_title1_en', 'b_title2_en', 'b_title3_en', 'b_title1_bn', 'b_title2_bn', 'b_title3_bn', 'b_alt'],
    tga: ['t_title_en', 't_title_bn', 't_alt'],
    about: ['a_title_en', 'a_title_bn', 'a_alt'],
    doctor: ['d_name_en', 'd_name_bn', 'd_designation_en', 'd_designation_bn', 'd_title_en', 'd_title_bn', 'd_description_en', 'd_description_bn', 'd_alt'],
    pedorthist: ['p_title_en', 'p_title_bn'],
  };

  // which file belongs to which DB key (if any)
  const fileFieldMap = {
    banner: { field: 'b_image', file: () => bImageFile, preview: () => bImagePreview },
    tga: { field: 't_image', file: () => tImageFile, preview: () => tImagePreview },
    about: { field: 'a_image', file: () => aImageFile, preview: () => aImagePreview },
    doctor: { field: 'd_image', file: () => dImageFile, preview: () => dImagePreview },
    pedorthist: null,
  };

  const submitSection = (section) => {
    if (!id) {
      swal('Error', 'No landing content selected to update', 'error');
      return;
    }

    // set section loader
    setSectionLoading(prev => ({ ...prev, [section]: true }));
    setErrors(prev => ({ ...prev }));

    const formData = new FormData();
    formData.append('_method', 'PUT');

    const keys = keysBySection[section] || [];
    keys.forEach(key => {
      const val = content[key];
      if (val !== undefined && val !== null) {
        formData.append(key, val);
      }
    });

    // append file for this section (if any)
    const fileMap = fileFieldMap[section];
    if (fileMap) {
      const fileObj = fileMap.file();
      const filePreview = fileMap.preview();
      if (fileObj) {
        formData.append(fileMap.field, fileObj);
      } else if (!filePreview) {
        // if preview cleared and no new file, send remove flag
        formData.append(`remove_${fileMap.field}`, '1');
      }
    }

    http
      .post(`/landing-page-content/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(res => {
        setSectionLoading(prev => ({ ...prev, [section]: false }));
        if (res.data && (res.data.status === 422 || res.status === 422)) {
          setErrors(res.data.error || {});
          swal('Validation Error', 'Please check the form fields', 'warning');
          return;
        }
        swal('Success', res.data.msg || `${section} updated`, 'success');
      })
      .catch(err => {
        setSectionLoading(prev => ({ ...prev, [section]: false }));
        console.error('Section update error:', err);
        if (err.response && err.response.data) {
          const resp = err.response.data;
          if (resp.status === 422 && resp.error) {
            setErrors(resp.error);
            swal('Validation Error', 'Please check the form fields', 'warning');
            return;
          }
        }
        swal('Error', 'Server error while updating', 'error');
      });
  };

  const submitForm = () => {
    if (!id) {
      swal('Error', 'No landing content selected to update', 'error');
      return;
    }

    setLoader('on');
    setErrors({});

    const formData = new FormData();
    formData.append('_method', 'PUT');

    // all keys to send (DB fields)
    const keysToSend = [
      'b_title1_en', 'b_title2_en', 'b_title3_en', 'b_title1_bn', 'b_title2_bn', 'b_title3_bn', 'b_alt',
      't_title_en', 't_title_bn', 't_alt',
      'a_title_en', 'a_title_bn', 'a_alt',
      'd_name_en', 'd_name_bn', 'd_designation_en', 'd_designation_bn', 'd_title_en', 'd_title_bn', 'd_description_en', 'd_description_bn', 'd_alt',
      'p_title_en', 'p_title_bn'
    ];

    keysToSend.forEach(key => {
      const val = content[key];
      if (val !== undefined && val !== null) {
        formData.append(key, val);
      }
    });

    // files
    if (bImageFile) formData.append('b_image', bImageFile);
    if (tImageFile) formData.append('t_image', tImageFile);
    if (aImageFile) formData.append('a_image', aImageFile);
    if (dImageFile) formData.append('d_image', dImageFile);

    // remove flags
    if (!bImagePreview && !bImageFile) formData.append('remove_b_image', '1');
    if (!tImagePreview && !tImageFile) formData.append('remove_t_image', '1');
    if (!aImagePreview && !aImageFile) formData.append('remove_a_image', '1');
    if (!dImagePreview && !dImageFile) formData.append('remove_d_image', '1');

    http
      .post(`/landing-page-content/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(res => {
        setLoader('off');
        if (res.data && (res.data.status === 422 || res.status === 422)) {
          setErrors(res.data.error || {});
          swal('Validation Error', 'Please check the form fields', 'warning');
          return;
        }
        swal('Success', res.data.msg || 'Updated successfully', 'success');
      })
      .catch(err => {
        setLoader('off');
        console.error('Update error:', err);
        if (err.response && err.response.data) {
          const resp = err.response.data;
          if (resp.status === 422 && resp.error) {
            setErrors(resp.error);
            swal('Validation Error', 'Please check the form fields', 'warning');
            return;
          }
        }
        swal('Error', 'Server error while updating', 'error');
      });
  };

  // helper to render per-section update button UI (show spinner if loading)
  const renderSectionButton = (sectionKey, label) => {
    const isLoading = !!sectionLoading[sectionKey];
    return (
      <div className="mt-3 d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-md btn-primary me-2"
          onClick={() => submitSection(sectionKey)}
          disabled={isLoading}
        >
          {isLoading ? <span className="spinner-border spinner-border-sm" role="status" /> : `Update ${label}`}
        </button>
      </div>
    );
  };

  return (
    <div>
      <div className="main-content" style={{ marginTop: -20 }}>

        <section className="section">
          <div className="section-body">
            <div className="row">
              <div className="col-12">

              <AdminQuickNav active="content" />

                <div className="card card-primary">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="">Landing Page Content - Edit</h5>
                      <BackButton />
                    </div>


                    <div className="row">

                      {/* Banner */}
                      <div className="col-12">
                        <fieldset className="border border-3 rounded p-4 mb-4 shadow-sm" style={{ borderColor: "#a8a8a8" }}>
                          <legend className="w-auto" style={{ fontSize: "15px", fontWeight: "600", padding: "2px 12px", color: "#333", border: "1px solid #ccc", borderRadius: "6px", background: "#f9f9f9", marginBottom: "0px" }}>
                            ⭐ Landing Page Banner
                          </legend>

                          <div className="row g-2">
                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Title 1 (English)</label>
                              <input name="b_title1_en" value={content.b_title1_en} onChange={handleInputChange} className="form-control mb-3" />
                              {errors.b_title1_en && <small className="text-danger">{errors.b_title1_en[0]}</small>}
                            </div>

                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Title 1 (Bangla)</label>
                              <input name="b_title1_bn" value={content.b_title1_bn} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Title 2 (English)</label>
                              <input name="b_title2_en" value={content.b_title2_en} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Title 2 (Bangla)</label>
                              <input name="b_title2_bn" value={content.b_title2_bn} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Title 3 (English)</label>
                              <input name="b_title3_en" value={content.b_title3_en} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Title 3 (Bangla)</label>
                              <input name="b_title3_bn" value={content.b_title3_bn} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Image</label>
                              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, "b_image")} className="form-control" />
                              {errors.b_image && <small className="text-danger d-block">{errors.b_image[0]}</small>}
                              {bImagePreview && <img src={bImagePreview} alt="banner preview" className="img-fluid mt-2 rounded" style={{ maxHeight: 120 }} />}

                            </div>

                            <div className="col-md-6">
                              <label className="form-label mb-1 fw-semibold">Banner Alt Text</label>
                              <input name="b_alt" value={content.b_alt} onChange={handleInputChange} className="form-control" />
                            </div>
                          </div>

                          {renderSectionButton('banner', 'Banner')}
                        </fieldset>
                      </div>

                      {/* TGA */}
                      <div className="col-12">
                        <fieldset className="border border-3 rounded p-4 mb-4 shadow-sm" style={{ borderColor: "#a8a8a8" }}>
                          <legend className="w-auto" style={{ fontSize: "15px", fontWeight: "600", padding: "2px 12px", color: "#333", border: "1px solid #ccc", borderRadius: "6px", background: "#f9f9f9", marginBottom: "0px" }}>
                            ⭐ TGA Approved Section
                          </legend>

                          <div className="row g-3">
                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">TGA Description (English)</label>
                              <JoditEditor
                                value={content.t_title_en}
                                config={editorConfig}
                                className='mb-3'
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('t_title_en', newContent)}
                                onChange={() => { }}
                              />
                            </div>

                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">TGA Description (Bangla)</label>
                              <JoditEditor
                                value={content.t_title_bn}
                                config={editorConfig}
                                className='mb-3'
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('t_title_bn', newContent)}
                                onChange={() => { }}
                              />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">TGA Logo</label>
                              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, "t_image")} className="form-control" />
                              {errors.t_image && <small className="text-danger d-block">{errors.t_image[0]}</small>}
                              {tImagePreview && <img src={tImagePreview} alt="tga preview" className="img-fluid mt-2 rounded" style={{ maxHeight: 120 }} />}

                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Logo ALT Text</label>
                              <input name="t_alt" value={content.t_alt} onChange={handleInputChange} className="form-control" />
                            </div>
                          </div>

                          {renderSectionButton('tga', 'TGA')}
                        </fieldset>
                      </div>

                      {/* About */}
                      <div className="col-12">
                        <fieldset className="border border-3 rounded p-4 mb-4 shadow-sm" style={{ borderColor: "#a8a8a8" }}>
                          <legend className="w-auto" style={{ fontSize: "15px", fontWeight: "600", padding: "2px 12px", color: "#333", border: "1px solid #ccc", borderRadius: "6px", background: "#f9f9f9", marginBottom: "0px" }}>
                            ⭐ About Us Section
                          </legend>

                          <div className="row g-3">
                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">About Us (English)</label>
                              <JoditEditor
                                value={content.a_title_en}
                                config={editorConfig}
                                className='mb-3'
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('a_title_en', newContent)}
                                onChange={() => { }}
                              />
                            </div>

                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">About Us (Bangla)</label>
                              <JoditEditor
                                value={content.a_title_bn}
                                config={editorConfig}
                                className='mb-3'
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('a_title_bn', newContent)}
                                onChange={() => { }}
                              />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">About Image</label>
                              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, "a_image")} className="form-control" />
                              {errors.a_image && <small className="text-danger d-block">{errors.a_image[0]}</small>}
                              {aImagePreview && <img src={aImagePreview} alt="about preview" className="img-fluid mt-2 rounded" style={{ maxHeight: 120 }} />}

                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">About Image Alt Text</label>
                              <input name="a_alt" value={content.a_alt} onChange={handleInputChange} className="form-control" />
                            </div>
                          </div>

                          {renderSectionButton('about', 'About')}
                        </fieldset>
                      </div>

                      {/* Doctor */}
                      <div className="col-12">
                        <fieldset className="border border-3 rounded p-4 mb-4 shadow-sm" style={{ borderColor: "#a8a8a8" }}>
                          <legend className="w-auto" style={{ fontSize: "15px", fontWeight: "600", padding: "2px 12px", color: "#333", border: "1px solid #ccc", borderRadius: "6px", background: "#f9f9f9", marginBottom: "0px" }}>
                            ⭐ Doctor Section
                          </legend>

                          <div className="row g-3">
                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Doctor Name (English)</label>
                              <input name="d_name_en" value={content.d_name_en} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Doctor Name (Bangla)</label>
                              <input name="d_name_bn" value={content.d_name_bn} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Designation (English)</label>
                              <input name="d_designation_en" value={content.d_designation_en} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Designation (Bangla)</label>
                              <input name="d_designation_bn" value={content.d_designation_bn} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Doctor Title (English)</label>
                              <input name="d_title_en" value={content.d_title_en} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Doctor Title (Bangla)</label>
                              <input name="d_title_bn" value={content.d_title_bn} onChange={handleInputChange} className="form-control mb-3" />
                            </div>

                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">Doctor Description (English)</label>
                              <JoditEditor
                                value={content.d_description_en}
                                config={editorConfig}
                                className='mb-3'
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('d_description_en', newContent)}
                                onChange={() => { }}
                              />
                            </div>

                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">Doctor Description (Bangla)</label>
                              <JoditEditor
                                value={content.d_description_bn}
                                config={editorConfig}
                                className='mb-3'
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('d_description_bn', newContent)}
                                onChange={() => { }}
                              />
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Doctor Image</label>
                              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, "d_image")} className="form-control" />
                              {dImagePreview && <img src={dImagePreview} alt="doctor preview" className="img-fluid mt-2 rounded" style={{ maxHeight: 120 }} />}
                            </div>

                            <div className="col-md-6">
                              <label className="form-label fw-semibold mb-1">Image ALT Text</label>
                              <input name="d_alt" value={content.d_alt} onChange={handleInputChange} className="form-control" />
                            </div>
                          </div>

                          {renderSectionButton('doctor', 'Doctor')}
                        </fieldset>
                      </div>

                      {/* Pedorthist (uses DB p_title_*) */}
                      <div className="col-12">
                        <fieldset className="border border-3 rounded p-4 mb-4 shadow-sm" style={{ borderColor: "#a8a8a8" }}>
                          <legend className="w-auto" style={{ fontSize: "15px", fontWeight: "600", padding: "2px 12px", color: "#333", border: "1px solid #ccc", borderRadius: "6px", background: "#f9f9f9", marginBottom: "0px" }}>
                            ⭐ Who is a Pedorthist
                          </legend>

                          <div className="row g-3">
                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">Pedorthist Description (English)</label>
                              <JoditEditor
                                value={content.p_title_en}
                                config={editorConfig}
                                className='mb-3'
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('p_title_en', newContent)}
                                onChange={() => { }}
                              />
                            </div>

                            <div className="col-md-12">
                              <label className="form-label fw-semibold mb-1">Pedorthist Description (Bangla)</label>
                              <JoditEditor
                                value={content.p_title_bn}
                                config={editorConfig}
                                tabIndex={1}
                                onBlur={newContent => handleEditorBlur('p_title_bn', newContent)}
                                onChange={() => { }}
                              />
                            </div>
                          </div>

                          {renderSectionButton('pedorthist', 'Pedorthist')}
                        </fieldset>
                      </div>

                    </div>


                    <div className="text-center my-3">
                      {loader === 'off' ? (
                        <button type="button" className="btn btn-primary" onClick={submitForm}>Update Landing Page Content</button>
                      ) : (
                        <div className="spinner-border text-dark" role="status"></div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LandingContentEdit;
